import type {
  TestPlayerAddResult,
  TestPlayersAddResponse,
} from '../../lib/api/operatorConsole';
import { Badge, type BadgeTone } from './Badge';
import { MatchBadge } from './MatchBadge';

interface Props {
  result: TestPlayersAddResponse;
  onDismiss: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  added: 'Added',
  reactivated: 'Reactivated',
  already_present: 'Already on the list',
  invalid: 'Invalid',
};

const STATUS_TONE: Record<string, BadgeTone> = {
  added: 'success',
  reactivated: 'success',
  already_present: 'neutral',
  invalid: 'error',
};

// An id that matched no session is accepted by the backend but excludes
// nobody. Nothing reports that later, so it has to be loud here.
function unmatched(entry: TestPlayerAddResult): boolean {
  return entry.status !== 'invalid' && entry.match === 'unknown';
}

function details(entry: TestPlayerAddResult): string {
  if (entry.status === 'invalid') return entry.message || entry.error || 'Could not be read.';
  if (entry.match === 'case_corrected' && entry.account_id) {
    return `Stored as "${entry.account_id}" to match your session history.`;
  }
  if (entry.match === 'unknown') {
    const variants = entry.case_variants ?? [];
    return variants.length
      ? `No exact match. Similar ids in your sessions: ${variants.join(', ')}.`
      : 'No session has ever used this id.';
  }
  return '';
}

export function AddResultPanel({ result, onDismiss }: Props) {
  const { added, reactivated, already_present: alreadyPresent } = result.summary;
  const invalid =
    result.summary.invalid ??
    result.results.filter((entry) => entry.status === 'invalid').length;
  const unmatchedEntries = result.results.filter(unmatched);

  const counts = [
    `${added} added`,
    `${reactivated} reactivated`,
    `${alreadyPresent} already on the list`,
    ...(invalid ? [`${invalid} invalid`] : []),
  ].join(' • ');

  return (
    <section
      aria-label="Add result"
      className="mb-5 max-w-3xl rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-4"
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-[13px] font-semibold text-[var(--co-text)]">
          Result — <span className="font-normal text-[var(--co-muted)]">{counts}</span>
        </h2>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] font-medium text-[var(--co-muted)] hover:text-[var(--co-text)]"
        >
          Dismiss
        </button>
      </div>

      {unmatchedEntries.length > 0 && (
        <div
          role="alert"
          className="mb-3 rounded-lg px-3 py-2 text-[12px]"
          style={{
            color: 'var(--co-error)',
            backgroundColor: 'color-mix(in srgb, var(--co-error) 10%, transparent)',
          }}
        >
          <strong className="font-semibold">
            {unmatchedEntries.length} id{unmatchedEntries.length === 1 ? '' : 's'} matched no
            session.
          </strong>{' '}
          We kept {unmatchedEntries.length === 1 ? 'it' : 'them'}, but if this is a typo the
          player keeps counting in your reports and nothing will tell you later. Check{' '}
          <span className="font-mono">
            {unmatchedEntries.map((entry) => entry.submitted).join(', ')}
          </span>
          .
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--co-border)]">
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
                Submitted
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
                Status
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
                Match
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--co-muted)]">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {result.results.map((entry, index) => (
              <tr
                key={`${entry.submitted}-${index}`}
                className="border-b border-[var(--co-border)] last:border-0"
              >
                <td className="px-3 py-2 font-mono text-[12px] text-[var(--co-text)]">
                  {entry.submitted}
                </td>
                <td className="px-3 py-2">
                  <Badge tone={STATUS_TONE[entry.status] ?? 'neutral'}>
                    {STATUS_LABEL[entry.status] ?? entry.status}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  {entry.status !== 'invalid' && entry.match && (
                    <MatchBadge matchStatus={entry.match} accountId={entry.account_id} />
                  )}
                </td>
                <td className="px-3 py-2 text-[12px] text-[var(--co-muted)]">
                  {details(entry) || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
